import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { countryCodes } from '../mock';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import { fetchGeolocation, submitLead } from '../services/apiService';

const PopupForm = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [geoData, setGeoData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+1',
    contactNumber: '',
    message: ''
  });

  useEffect(() => {
    // Fetch geolocation when popup opens
    if (isOpen && !geoData) {
      const loadGeolocation = async () => {
        const result = await fetchGeolocation();
        if (result.success) {
          setGeoData(result.data);
        }
      };
      loadGeolocation();
    }
  }, [isOpen, geoData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Validate phone number (10 digits only)
    if (!/^\d{10}$/.test(formData.contactNumber)) {
      toast.error('Please enter a valid 10-digit phone number');
      setIsSubmitting(false);
      return;
    }
    
    // Find country from countryCodes
    const selectedCountry = countryCodes.find(c => c.code === formData.countryCode);
    const dialCode = selectedCountry ? parseInt(selectedCountry.code.replace('+', '')) : 1;
    const country = selectedCountry ? selectedCountry.country.split(' ')[0].substring(0, 2).toUpperCase() : 'US';

    const leadData = {
      name: formData.name,
      email: formData.email,
      countryDialCode: dialCode,
      country,
      phoneNumber: formData.contactNumber,
      ipAddress: geoData?.ipAddress || '',
      description: formData.message || '',
      city: geoData?.cityName || '',
      state: geoData?.regionName || '',
      geoLocation: geoData ? {
        isSuccess: true,
        data: geoData,
        errorMessage: '',
        statusCode: 200
      } : null
    };

    const result = await submitLead(leadData);
    setIsSubmitting(false);

    if (result.success) {
      toast.success(result.message || 'Thank you! We will contact you shortly.');
      setFormData({
        name: '',
        email: '',
        countryCode: '+1',
        contactNumber: '',
        message: ''
      });
      onClose();
      // Redirect to application page
      // navigate(`/application/main?customerid=${result.customerId}`);
      window.location = `/application/main?customerid=${result.customerId}`
      
    } else {
      toast.error(result.error || 'Failed to submit. Please try again.');
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePhoneChange = (e) => {
    // Only allow digits
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 10) {
      handleChange('contactNumber', value);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-gray-900">
            Get Started Today
          </DialogTitle>
        </DialogHeader>
        {geoData && (
          <div className="mt-2 p-3 bg-[#E2F8FE] rounded-lg border border-[#FF4A2A]/20">
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <span className="font-semibold">📍 Detected Location:</span>
              <span>{geoData.countryName}</span>
            </div>
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <Label htmlFor="name" className="text-base">Enter Your Name *</Label>
            <Input
              id="name"
              type="text"
              required
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Enter your full name"
              className="mt-1 text-base"
            />
          </div>

          <div>
            <Label htmlFor="email" className="text-base">Enter Your Email *</Label>
            <Input
              id="email"
              type="email"
              required
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="Enter your email address"
              className="mt-1 text-base"
            />
          </div>

          <div>
            <Label htmlFor="contact" className="text-base">Number *</Label>
            <div className="flex gap-2 mt-1">
              <Select
                value={formData.countryCode}
                onValueChange={(value) => handleChange('countryCode', value)}
              >
                <SelectTrigger className="w-[120px] text-base">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {countryCodes.map((item) => (
                    <SelectItem key={item.code} value={item.code} className="text-base">
                      {item.code} ({item.country})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                id="contact"
                type="text"
                required
                value={formData.contactNumber}
                onChange={handlePhoneChange}
                placeholder="Enter Your Number"
                className="flex-1 text-base"
                maxLength={10}
                pattern="\d{10}"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="message" className="text-base">What are you looking for?</Label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(e) => handleChange('message', e.target.value)}
              placeholder="e.g., name, logo, slogan"
              rows={4}
              className="mt-1 text-base"
            />
          </div>

          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-[#FF4A2A] hover:bg-[#E63E1F] disabled:bg-gray-400 text-white py-6 text-lg font-semibold"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Request'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PopupForm;